// src/components/DashboardHome.tsx
import React, { useEffect, useState } from 'react';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { getConversationList } from '../services/conversationService';
import { AlertTriangle } from 'lucide-react';
import LoadingIndicator from './LoadingIndicator';
import '../styles/DashboardHome.css';

/** 型定義 -------------------------------------- */
interface User {
  id: number;
  username: string;
  email: string;
  contact?: string;
  auto_response_enabled?: boolean;
}

interface DashboardHomeProps {
  user: User | null;
}

/** コンポーネント -------------------------------- */
const DashboardHome: React.FC<DashboardHomeProps> = ({ user }) => {
  /* ユーザープロフィール */
  const [profile, setProfile] = useState({
    username: '',
    email: '',
    contact: '',
  });
  useEffect(() => {
    if (user) {
      setProfile({
        username: user.username,
        email: user.email,
        contact: user.contact ?? '',
      });
    }
  }, [user]);

  /* サブスクリプション情報 */
  const { data: subscription, isLoading: isSubLoading } =
    useSubscriptionStatus();

  const [loading, setLoading] = useState<boolean>(true);
  useEffect(() => {
    setLoading(isSubLoading);
  }, [isSubLoading]);

  // チャット履歴を取得
  const [convCount, setConvCount] = useState<number | null>(null);
  useEffect(() => {
    (async () => {
      try {
        const res = await getConversationList();
        setConvCount(res.data.length);
      } catch (e) {
        console.error(e);
        setConvCount(null);
      }
    })();
  }, []);

  /* 現在プランの計算 */
  const plan = subscription?.plan;
  const blockCount = subscription?.block_count ?? 0; // 契約ブロック
  const totalBlocks = subscription?.total_blocks ?? 0; // 契約+追加
  const blockSize = plan?.block_size ?? 0; // 1ブロックあたり通数
  const used = subscription?.usage_count ?? 0;
  const limit = blockSize * totalBlocks;
  const nearLimit = limit > 0 && used / limit >= 0.8; // 80% 以上でアラート

  // プラン変更の予約
  const hasPendingChange = Boolean(subscription?.upcoming_plan);
  const nextDate = subscription?.upcoming_effective_at
    ? new Date(subscription.upcoming_effective_at).toLocaleDateString()
    : '';
  const nextUpdateDate = subscription?.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString()
    : '';
  const nextPlanName = subscription?.upcoming_plan?.name;
  const nextBlocks = subscription?.upcoming_block_count ?? 0;

  // キャンセル予約中か（バックエンドの is_cancel_scheduled フラグ）
  const isCancelScheduled = subscription?.is_cancel_scheduled ?? false;
  // 解約予定・解約日
  const cancelDate = subscription?.canceled_at
    ? new Date(subscription.canceled_at).toLocaleDateString()
    : '';

  // 自動応答設定
  const autoResponseEnabled = user?.auto_response_enabled ?? false;

  /* 表示 --------------------------------------- */
  return (
    <div className="dashboard-home">
      <p>こんにちは、{profile.username || 'ゲスト'} さん</p>
      {loading ? (
        <LoadingIndicator size={40} />
      ) : !subscription || subscription.status === 'canceled' ? (
        /* ── 新規契約 ── */
        <section className="new-subscription">
          <h2 className="section-title">新規契約</h2>
          <p>現在、サブスクリプションはありません。</p>
          <p className="pc_disp_db">左メニューの「サブスクリプション管理」から設定できます。</p>
          <p className="sp_disp_db">メニューの「サブスクリプション管理」から設定できます。</p>
        </section>
      ) : (
        <>
          <section className="current-plan">
            <h2 className="section-title">現在のプラン</h2>
            <table>
              <tbody>
                <tr>
                  <th>プラン名</th>
                  <td>{plan?.name}</td>
                </tr>
                <tr>
                  <th>メッセージ数上限</th>
                  <td>{blockCount * blockSize} 通</td>
                </tr>
                <tr>
                  <th>追加購入したメッセージ</th>
                  <td>{(totalBlocks - blockCount) * blockSize} 通</td>
                </tr>
                <tr>
                  <th>利用状況</th>
                  <td>
                    {used} / {limit} 通
                    {nearLimit && (
                      <span className="near-limit">
                        <AlertTriangle size={16} /> 上限が近づいています
                      </span>
                    )}
                  </td>
                </tr>
                {subscription!.trial_ends_at &&
                  new Date(subscription!.trial_ends_at) > new Date() && (
                    <tr>
                      <th>トライアル終了日</th>
                      <td>
                        {new Date(
                          subscription.trial_ends_at
                        ).toLocaleDateString()}
                      </td>
                    </tr>
                  )}
                {(!isCancelScheduled && !hasPendingChange) && (
                  <tr>
                    <th>次回更新日</th>
                    <td>
                      {nextUpdateDate
                        ? new Date(nextUpdateDate).toLocaleDateString()
                        : '-'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {hasPendingChange && (
              <>
                <h3>次回更新時のプラン</h3>
                <div className="current-plan-details">
                  <table>
                    <tbody>
                      <tr>
                        <th>プラン名</th>
                        <td>{nextPlanName || 'なし'}</td>
                      </tr>
                      <tr>
                        <th>メッセージ数上限</th>
                        <td>{nextBlocks * subscription!.plan.block_size} 通</td>
                      </tr>
                      <tr>
                        <th>次回更新日</th>
                        <td>{nextDate || '未定'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {subscription!.trial_ends_at &&
              new Date(subscription!.trial_ends_at) > new Date() && (
                <p>
                  トライアル期間：
                  {new Date(subscription!.trial_ends_at).toLocaleDateString()}
                </p>
              )}
            {isCancelScheduled && (
              /* ── キャンセル予約中 ── */
              <p className="sub-cancel-info">
                <strong>解約申請中</strong>
                <br />
                解約予定日：<strong>{cancelDate}</strong>
              </p>
            )}
          </section>
          <section className="other">
            <h2 className="section-title">応答状況</h2>
            <p>AIによる自動応答：{autoResponseEnabled ? 'ON' : 'OFF'}</p>
            <p>自動応答したユーザー数：{convCount ?? '-'}</p>
            <a href="/instruction" target="_blank" rel="noreferrer">
              設定マニュアルはこちら
            </a>
          </section>
        </>
      )}
    </div>
  );
};

export default DashboardHome;
